import { useEffect, useState } from "react";
import type { Book } from "../api";
import { useT } from "../i18n";
import { ShareIcon } from "./icons";

/**
 * Whether the book page offers Share: the server's `shareable`, which is the
 * link preview's own rule (ADR-0209 §3), so a shared link always previews the
 * book's face. Never re-derived here from visibility and publication.
 */
export function shareable(book: Pick<Book, "url" | "shareable">): boolean {
  return !!book.url && book.shareable === true;
}

/** "‹title› — ‹author›", each isolated so a Persian title keeps its order. */
export function bookShareTitle(book: Pick<Book, "title" | "author">): string {
  const title = `⁨${book.title}⁩`;
  return book.author ? `${title} — ⁨${book.author}⁩` : title;
}

/**
 * Share on the book page (requirement 0138, owner 10-25): the browser's own
 * share where there is one, else the link copied with a short note. The link
 * is the server's (ADR-0209), never built here.
 */
export default function ShareBook({ book }: { book: Book }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(id);
  }, [copied]);

  if (!shareable(book)) return null;
  const url = book.url!;

  const share = async () => {
    if (typeof navigator.share === "function") {
      // A cancelled sheet rejects; nothing to say about it.
      await navigator.share({ title: book.title, text: bookShareTitle(book), url }).catch(() => {});
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // No clipboard either (an insecure origin): nothing to offer.
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid="share-book"
        onClick={() => void share()}
        className="inline-flex items-center gap-2 rounded-control bg-sunk px-3.5 py-2 text-sm font-medium text-ink-soft transition-colors hover:bg-sunk/70 hover:text-ink"
      >
        <ShareIcon className="size-4" />
        {t.book.share}
      </button>
      {copied && (
        <p
          role="status"
          data-testid="share-book-copied"
          className="absolute start-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap text-xs text-muted rtl:translate-x-1/2"
        >
          {t.book.linkCopied}
        </p>
      )}
    </div>
  );
}
